export function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
}

// =====================================================================
// Locale-aware + usage staleness helpers (t_3ce8079f + scope extensions).
// =====================================================================

/** Staleness threshold (scope extension 2) — never a magic number inline. */
export const STALE_THRESHOLD_DAYS = 30;

export type UsageLevel =
    | 'fresh' // fetched within the threshold window
    | 'stale' // last fetch older than the threshold (amber)
    | 'never' // null lastFetchedAt, created recently (grey)
    | 'never-pretracking'; // null lastFetchedAt, created long ago (grey, distinct tooltip)

export interface UsageStatus {
    level: UsageLevel;
    daysSinceCreation: number;
    daysSinceFetch: number | null;
}

/**
 * Classify a secret's usage state for list/detail display. null
 * lastFetchedAt means "not fetched since tracking began", judged relative to
 * createdAt: recently created = 'never'; old + never fetched = likely
 * pre-tracking (or genuinely never used — the tooltip distinguishes, the
 * styling is grey for both).
 */
export function usageStatus(secret: { createdAt: string; lastFetchedAt: string | null }): UsageStatus {
    const daysSinceCreation = Math.floor(
        (Date.now() - new Date(secret.createdAt).getTime()) / 86400000
    );
    if (secret.lastFetchedAt === null) {
        return {
            level: daysSinceCreation >= STALE_THRESHOLD_DAYS ? 'never-pretracking' : 'never',
            daysSinceCreation,
            daysSinceFetch: null,
        };
    }
    const daysSinceFetch = Math.floor(
        (Date.now() - new Date(secret.lastFetchedAt).getTime()) / 86400000
    );
    return {
        level: daysSinceFetch >= STALE_THRESHOLD_DAYS ? 'stale' : 'fresh',
        daysSinceCreation,
        daysSinceFetch,
    };
}

// SP-295D4: region-tag map is DATA (not a per-language switch); unknown
// activated locales pass through to Intl directly.
const DATE_REGION_TAGS: Record<string, string> = {
    en: 'en-GB',
    nb: 'nb-NO',
};

function dateLocale(locale: string): string {
    return DATE_REGION_TAGS[locale] ?? locale;
}

/** Relative time in the active locale ("3 minutes ago" / "for 3 minutter siden"). */
export function formatRelativeLocale(iso: string | null | undefined, locale: string): string {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const rtf = new Intl.RelativeTimeFormat(dateLocale(locale), { numeric: 'auto' });
    const sec = Math.round(diff / 1000);
    if (Math.abs(sec) < 60) return rtf.format(-sec, 'second');
    const min = Math.round(sec / 60);
    if (Math.abs(min) < 60) return rtf.format(-min, 'minute');
    const hr = Math.round(min / 60);
    if (Math.abs(hr) < 24) return rtf.format(-hr, 'hour');
    return rtf.format(-Math.round(hr / 24), 'day');
}

/** Absolute timestamp in the active locale. */
export function formatDateLocale(iso: string | null | undefined, locale: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(dateLocale(locale));
}

export function formatRelative(iso: string | null | undefined): string {
    if (!iso) return 'never';
    const diff = Date.now() - new Date(iso).getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
}

export function formatBytes(s: string | number | null | undefined): string {
    if (!s) return '—';
    let n = typeof s === 'string' ? Number(s) : s;
    if (!Number.isFinite(n)) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(1)} ${units[i]}`;
}

export function expirationStatus(iso: string | null): {
    level: 'ok' | 'warn' | 'danger' | 'expired';
    daysLeft: number | null;
    label: string;
} {
    if (!iso) return { level: 'ok', daysLeft: null, label: '' };
    const days = Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0)  return { level: 'expired', daysLeft: days, label: `Expired ${-days}d ago` };
    if (days <= 7) return { level: 'danger',  daysLeft: days, label: `Expires in ${days}d` };
    if (days <= 30)return { level: 'warn',    daysLeft: days, label: `Expires in ${days}d` };
    return { level: 'ok', daysLeft: days, label: `Expires in ${days}d` };
}